import { skipToken } from "@reduxjs/toolkit/query";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useGetEngineExplanationQuery,
  useGetLatestEngineExplanationQuery,
  useRequestEngineExplanationMutation,
} from "store/queries/benefitComparison";
import {
  ComparisonBenefit,
  IEngineExplanation,
} from "types/BenefitComparison/BenefitComparison";

import ChatMarkdown from "../ContractModelDrafts/wizard/chatMarkdown";
import { COMPARISON_BENEFIT_LABELS } from "./benefitComparisonWording";
import { relativeTime } from "./remitAccuracyWording";

export const EXPLAIN_BUTTON_LABEL = "Ask Cyrus to Explain";
export const ASK_AGAIN_LABEL = "Ask Cyrus again";
export const TRY_AGAIN_LABEL = "Try again";
export const ASK_SUBMIT_LABEL = "Ask Cyrus";
export const QUESTION_LABEL =
  "Anything specific you want Cyrus to look at? (optional)";
export const QUESTION_HINT =
  "Don't include patient names, dates of birth or member IDs.";
/** The server enforces the same limit (400 past it). */
export const MAX_QUESTION_CHARS = 1000;
export const ALREADY_WORKING_TEXT =
  "Cyrus is already working on this one, so your question wasn't sent. Ask again once it finishes.";
export const STALE_TEXT =
  "The engine's settings changed since Cyrus wrote this.";
export const QUEUED_TEXT = "Waiting for Cyrus to pick this up…";
export const QUEUED_UNPICKED_TEXT =
  "Cyrus hasn't picked this up yet — the explanation worker may not be running.";
export const RUNNING_TEXT =
  "Cyrus is working on it — this can take a minute or more.";
export const STUCK_TEXT =
  "This looks stuck — Cyrus has been on it longer than its 10-minute budget.";
/** How often the panel re-reads a QUEUED / RUNNING explanation. */
export const POLL_MS = 4000;
/** How often the elapsed time ("queued 40 s ago") re-renders while waiting. */
export const TICK_MS = 5000;
/** A worker polls every few seconds, so a row still QUEUED after this was likely never picked up. */
export const QUEUED_UNPICKED_AFTER_MS = 2 * 60 * 1000;
/** The worker's budget is 10 minutes; past this a RUNNING row with no recent heartbeat is stuck. */
export const RUNNING_STUCK_AFTER_MS = 12 * 60 * 1000;
/** A live worker heartbeats every ~30 s; a heartbeat newer than this means Cyrus is still on it. */
const HEARTBEAT_FRESH_MS = 2 * 60 * 1000;

const isActive = (explanation: IEngineExplanation | null | undefined) =>
  explanation?.status === "QUEUED" || explanation?.status === "RUNNING";

const msSince = (iso: string | null, now: number) =>
  iso === null ? null : now - new Date(iso).getTime();

/** "40 s", "3 min" — the wait so far, to the second under a minute. */
export const formatElapsed = (ms: number) => {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  if (seconds < 60) return `${seconds} s`;
  return `${Math.floor(seconds / 60)} min`;
};

/**
 * RUNNING for longer than the worker's budget allows, and not heartbeating. A re-claimed second
 * attempt keeps the first attempt's `startedAt` but heartbeats, so it is not called stuck.
 */
export const isStuck = (explanation: IEngineExplanation, now: number) => {
  if (explanation.status !== "RUNNING") return false;
  const running = msSince(explanation.startedAt ?? explanation.createdAt, now);
  const heartbeat = msSince(explanation.heartbeatAt, now);
  return (
    running !== null &&
    running > RUNNING_STUCK_AFTER_MS &&
    (heartbeat === null || heartbeat > HEARTBEAT_FRESH_MS)
  );
};

/** `Date.now()`, re-read every `intervalMs` while `intervalMs > 0` — so elapsed times tick. */
const useNow = (intervalMs: number) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (intervalMs <= 0) return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return now;
};

interface IEngineExplanationContext {
  pverifyResponseId: string;
  /** pVerify's RequestID for the check, as the panel shows it ("Showing RequestID …"). */
  pverifyRequestId: string | null;
  benefit: ComparisonBenefit;
  explanation: IEngineExplanation | null | undefined;
  loading: boolean;
  isAsking: boolean;
  askFailed: boolean;
  now: number;
  /** The POST met a run already in flight, so the question that came with it was not sent. */
  alreadyWorking: boolean;
  request: (rerun: boolean, question?: string) => Promise<void>;
}

const EngineExplanationContext =
  createContext<IEngineExplanationContext | null>(null);

/**
 * "Ask Cyrus to Explain" — on the cluster page's response panel.
 *
 * One click queues a question for Cyrus (the agents app): why did the engine pick, or not pick,
 * a value for THIS check and THIS benefit. The worker gives Cyrus the identifiers and nothing
 * else; it reads the data and the code itself. The panel polls until the answer is in.
 *
 * Split in three so the panel can put the button in its toolbar and the answer above the table:
 * this provider holds the state (key it on the check + benefit), `ExplainButton` is the toolbar
 * button, and `EngineExplanationSection` is the output — nothing until there is something to show.
 *
 * - Nothing asked yet: the button, no section.
 * - QUEUED: "waiting to be picked up" and since when; past 2 minutes, that the worker may not be
 *   running (there is none on dev).
 * - RUNNING: "working on it" and since when; past 12 minutes with no heartbeat, that it looks stuck
 *   — and the button becomes "Try again".
 * - DONE: the answer, when it was written, and the button becomes "Ask Cyrus again" — with a line
 *   above it when the engine's settings changed since it was written (`stale`).
 * - FAILED: why, and the button becomes "Try again".
 *
 * Every button — "Ask Cyrus to Explain", "Ask Cyrus again" and "Try again" — opens the Ask Cyrus
 * dialog, with an optional question. Without one the request is exactly the old one (a plain ask
 * first, a rerun after); with one the server always queues a new run (a cached
 * answer did not address it) — unless a run is in flight, which it hands back instead, and the
 * panel says the question was not sent. The question is shown above the answer.
 *
 * 🚨 The panel tracks the row the POST returned, not just "the newest row": the POST can hand
 * back an older finished answer that is still current, and that is the one to show.
 */
export function EngineExplanationProvider({
  pverifyResponseId,
  pverifyRequestId = null,
  benefit,
  children,
}: {
  pverifyResponseId: string;
  /** The RequestID the panel shows — named in the dialog before any explanation row exists. */
  pverifyRequestId?: string | null;
  benefit: ComparisonBenefit;
  children: ReactNode;
}) {
  const [requestedId, setRequestedId] = useState<string | null>(null);
  const [latestPolling, setLatestPolling] = useState(0);
  const latest = useGetLatestEngineExplanationQuery(
    { pverifyResponseId, benefit },
    { pollingInterval: requestedId ? 0 : latestPolling }
  );
  const [byIdPolling, setByIdPolling] = useState(POLL_MS);
  const byId = useGetEngineExplanationQuery(requestedId ?? skipToken, {
    pollingInterval: byIdPolling,
  });
  const [ask, { isLoading: isAsking, isError: askFailed }] =
    useRequestEngineExplanationMutation();
  const [alreadyWorking, setAlreadyWorking] = useState(false);

  const explanation = requestedId ? byId.data?.data : latest.data?.data;
  const active = isActive(explanation);
  // Poll only while Cyrus is working; stop the moment the row settles.
  useEffect(() => {
    setLatestPolling(!requestedId && active ? POLL_MS : 0);
    setByIdPolling(requestedId && (!explanation || active) ? POLL_MS : 0);
  }, [requestedId, active, explanation]);
  // The elapsed time must move while waiting, whether or not a poll changed anything.
  const now = useNow(active ? TICK_MS : 0);

  const value = useMemo<IEngineExplanationContext>(
    () => ({
      pverifyResponseId,
      pverifyRequestId,
      benefit,
      explanation,
      loading: latest.isLoading || (requestedId !== null && !explanation),
      isAsking,
      askFailed,
      now,
      alreadyWorking,
      request: async (rerun: boolean, question?: string) => {
        const trimmed = question?.trim() ?? "";
        setAlreadyWorking(false);
        try {
          const result = await ask({
            pverifyResponseId,
            benefit,
            ...(rerun ? { rerun: true } : {}),
            ...(trimmed ? { question: trimmed } : {}),
          }).unwrap();
          setRequestedId(result.data.id);
          setAlreadyWorking(
            trimmed !== "" &&
              result.message === "reused" &&
              isActive(result.data)
          );
        } catch {
          // `askFailed` renders the message
        }
      },
    }),
    [
      explanation,
      latest.isLoading,
      requestedId,
      isAsking,
      askFailed,
      now,
      alreadyWorking,
      ask,
      pverifyResponseId,
      pverifyRequestId,
      benefit,
    ]
  );

  return (
    <EngineExplanationContext.Provider value={value}>
      {children}
    </EngineExplanationContext.Provider>
  );
}

const useEngineExplanation = () => useContext(EngineExplanationContext);

/**
 * "Ask Cyrus" — what every toolbar button opens: which check and benefit Cyrus will explain, an
 * optional question, Cancel and Ask Cyrus. Cancel sends nothing.
 */
function AskCyrusDialog({
  requestId,
  benefit,
  visible,
  onHide,
  onAsk,
  isAsking,
}: {
  requestId: string;
  benefit: ComparisonBenefit;
  visible: boolean;
  onHide: () => void;
  onAsk: (question: string) => Promise<void>;
  isAsking: boolean;
}) {
  const [question, setQuestion] = useState("");
  const close = () => {
    setQuestion("");
    onHide();
  };
  return (
    <Dialog
      header="Ask Cyrus"
      visible={visible}
      style={{ width: "min(32rem, 95vw)" }}
      onHide={close}
      data-testid="benefitComparison.cluster.explain.dialog"
      footer={
        <>
          <Button
            type="button"
            label="Cancel"
            className="p-button-text"
            onClick={close}
          />
          <Button
            type="button"
            label={ASK_SUBMIT_LABEL}
            icon="pi pi-comments"
            loading={isAsking}
            data-testid="benefitComparison.cluster.explain.dialog.ask"
            onClick={async () => {
              await onAsk(question);
              close();
            }}
          />
        </>
      }
    >
      <div className="flex flex-column gap-3">
        <span>
          Cyrus will explain the engine&apos;s{" "}
          {COMPARISON_BENEFIT_LABELS[benefit]} on check RequestID {requestId}.
        </span>
        <div className="flex flex-column gap-1">
          <label htmlFor="benefitComparison.cluster.explain.question">
            {QUESTION_LABEL}
          </label>
          <InputTextarea
            id="benefitComparison.cluster.explain.question"
            value={question}
            rows={4}
            autoResize
            maxLength={MAX_QUESTION_CHARS}
            onChange={(e) =>
              setQuestion(e.target.value.slice(0, MAX_QUESTION_CHARS))
            }
          />
          <div className="flex justify-content-between gap-2 text-sm text-color-secondary">
            <span>{QUESTION_HINT}</span>
            <span className="white-space-nowrap">
              {question.length} / {MAX_QUESTION_CHARS}
            </span>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * The toolbar button. Before anything is asked it is "Ask Cyrus to Explain"; while Cyrus works it
 * is busy; once there is an answer it is "Ask Cyrus again", and on a failure or a stuck run it is
 * "Try again". Each opens the Ask Cyrus dialog — the first one asks plainly, the others re-run. The only button that asks — the section below has none, so there are
 * never two doing the same thing.
 */
export function ExplainButton() {
  const context = useEngineExplanation();
  // Open with the rerun flag of the button that opened it; null = closed. One dialog for every
  // button, so it survives the button changing under it once the ask lands.
  const [dialogRerun, setDialogRerun] = useState<boolean | null>(null);
  if (!context) return null;
  const { explanation, loading, isAsking, now, request } = context;
  // The RequestID the panel shows; the row's own; the response id only when neither is known.
  const requestId =
    context.pverifyRequestId ??
    explanation?.pverifyRequestId ??
    context.pverifyResponseId;
  const dialog = (
    <AskCyrusDialog
      requestId={requestId}
      benefit={context.benefit}
      visible={dialogRerun !== null}
      onHide={() => setDialogRerun(null)}
      onAsk={(question) => request(dialogRerun ?? false, question)}
      isAsking={isAsking}
    />
  );

  const common = {
    type: "button" as const,
    icon: "pi pi-comments",
    className: "p-button-outlined p-button-sm",
    loading: isAsking,
  };

  if (explanation?.status === "DONE") {
    return (
      <>
        <Button
          {...common}
          label={ASK_AGAIN_LABEL}
          data-testid="benefitComparison.cluster.explain.again"
          onClick={() => setDialogRerun(true)}
        />
        {dialog}
      </>
    );
  }
  if (
    explanation &&
    (explanation.status === "FAILED" || isStuck(explanation, now))
  ) {
    return (
      <>
        <Button
          {...common}
          label={TRY_AGAIN_LABEL}
          data-testid="benefitComparison.cluster.explain.retry"
          onClick={() => setDialogRerun(true)}
        />
        {dialog}
      </>
    );
  }
  const waiting = loading || isActive(explanation);
  return (
    <>
      <Button
        {...common}
        label={EXPLAIN_BUTTON_LABEL}
        loading={isAsking || waiting}
        disabled={waiting}
        data-testid="benefitComparison.cluster.explain"
        onClick={() => setDialogRerun(false)}
      />
      {dialog}
    </>
  );
}

function Waiting({
  explanation,
  now,
}: {
  explanation: IEngineExplanation;
  now: number;
}) {
  if (explanation.status === "QUEUED") {
    const queuedFor = msSince(explanation.createdAt, now) ?? 0;
    return (
      <div data-testid="benefitComparison.cluster.explanation.working">
        <span>
          <i className="pi pi-spin pi-spinner mr-2" aria-hidden="true" />
          {QUEUED_TEXT}
        </span>
        <span className="text-sm text-color-secondary ml-2">
          queued {formatElapsed(queuedFor)} ago
        </span>
        {queuedFor > QUEUED_UNPICKED_AFTER_MS && (
          <p
            className="text-sm text-orange-700 mt-2 mb-0"
            data-testid="benefitComparison.cluster.explanation.unpicked"
          >
            {QUEUED_UNPICKED_TEXT}
          </p>
        )}
      </div>
    );
  }
  const runningFor =
    msSince(explanation.startedAt ?? explanation.createdAt, now) ?? 0;
  const stuck = isStuck(explanation, now);
  return (
    <div data-testid="benefitComparison.cluster.explanation.working">
      <span>
        <i
          className={`pi ${
            stuck ? "pi-exclamation-triangle" : "pi-spin pi-spinner"
          } mr-2`}
          aria-hidden="true"
        />
        {RUNNING_TEXT}
      </span>
      <span className="text-sm text-color-secondary ml-2">
        started {formatElapsed(runningFor)} ago
      </span>
      {stuck && (
        <p
          className="text-sm text-orange-700 mt-2 mb-0"
          data-testid="benefitComparison.cluster.explanation.stuck"
        >
          {STUCK_TEXT}
        </p>
      )}
    </div>
  );
}

/**
 * The output, under its own heading. Renders nothing until something has been asked, or a
 * previous answer for this check was found on open.
 */
export function EngineExplanationSection({
  className = "",
}: {
  className?: string;
}) {
  const context = useEngineExplanation();
  if (!context) return null;
  const { explanation, askFailed, alreadyWorking, now } = context;
  // While the previous answer (or the row just asked for) loads, the toolbar button is busy.
  if (!explanation && !askFailed) return null;

  return (
    <div
      className={className}
      data-testid="benefitComparison.cluster.explanation"
    >
      <div className="flex flex-wrap align-items-baseline justify-content-between gap-2 mb-2">
        <span className="text-xs font-bold uppercase text-color-secondary">
          Cyrus&apos;s explanation
        </span>
        {explanation?.status === "DONE" && (
          <span
            className="text-sm text-color-secondary"
            title={explanation.finishedAt ?? explanation.createdAt}
          >
            {relativeTime(explanation.finishedAt ?? explanation.createdAt)}
          </span>
        )}
      </div>

      {askFailed && (
        <Message
          severity="error"
          className="mb-2"
          text="Cyrus could not be asked. Try again."
        />
      )}

      {/* Only while that run is still going — once it settles, "Ask Cyrus again" is the way in. */}
      {alreadyWorking && isActive(explanation) && (
        <Message severity="info" className="mb-2" text={ALREADY_WORKING_TEXT} />
      )}

      {explanation?.question && (
        <p
          className="text-sm text-color-secondary font-italic mt-0 mb-2"
          style={{ whiteSpace: "pre-wrap" }}
          data-testid="benefitComparison.cluster.explanation.question"
        >
          You asked: {explanation.question}
        </p>
      )}

      {explanation && isActive(explanation) && (
        <Waiting explanation={explanation} now={now} />
      )}

      {explanation?.status === "DONE" && (
        <div data-testid="benefitComparison.cluster.explanation.answer">
          {/*
           * 🚨 An answer written under an older configuration can blame a setting that is gone (an
           * ignore keyword since removed). Say so above it rather than let it read as current.
           */}
          {explanation.stale && (
            <p
              className="text-sm text-orange-700 mt-0 mb-2"
              data-testid="benefitComparison.cluster.explanation.stale"
            >
              <i
                className="pi pi-exclamation-triangle mr-2"
                aria-hidden="true"
              />
              {STALE_TEXT}
            </p>
          )}
          <ChatMarkdown text={explanation.answer ?? ""} />
        </div>
      )}

      {explanation?.status === "FAILED" && (
        <div data-testid="benefitComparison.cluster.explanation.failed">
          <Message
            severity="warn"
            text={`Cyrus could not explain this check: ${
              explanation.error ?? "unknown error"
            }`}
          />
        </div>
      )}
    </div>
  );
}
